import React, { useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import RichTextEditor, { getQuillPlainLength } from 'components/RichTextEditor';
import FieldLabel from './FieldLabel';

/** Rich-text field with maxLength truncate + over-limit message (local UI state only). */
const HtmlFieldWithLimit = ({
    label,
    tooltip,
    value,
    onChange,
    maxLen,
    hasError,
    errorMessage,
}) => {
    const [showLimitError, setShowLimitError] = useState(false);
    const [plainLength, setPlainLength] = useState(() => getQuillPlainLength(value));
    const atLimit = Boolean(maxLen && plainLength >= maxLen);

    // Stable identity — RichTextEditor re-runs its length effect when this changes.
    const handlePlainLengthChange = useCallback((len) => {
        setPlainLength(len);
        if (maxLen && len < maxLen) {
            setShowLimitError(false);
        }
    }, [maxLen]);

    return (
        <Box>
            <MDTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.75}>
                <FieldLabel label={label} tooltip={tooltip} />
            </MDTypography>
            <Box
                sx={{
                    border: '1px solid',
                    borderColor: hasError || showLimitError ? 'error.main' : 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                }}
            >
                <RichTextEditor
                    value={value}
                    onChange={onChange}
                    onPlainLengthChange={handlePlainLengthChange}
                    height={180}
                    showAiGenerate={false}
                    maxLength={maxLen}
                    onMaxLengthExceeded={() => setShowLimitError(true)}
                />
            </Box>
            <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mt={0.5} gap={1}>
                <Box>
                    {hasError && (
                        <MDTypography variant="caption" color="error" display="block">
                            {errorMessage || 'This field is required'}
                        </MDTypography>
                    )}
                    {showLimitError && (
                        <MDTypography variant="caption" color="error" display="block">
                            Maximum {maxLen} characters allowed. Extra text was removed.
                        </MDTypography>
                    )}
                </Box>
                {maxLen && (
                    <MDTypography
                        variant="caption"
                        color={atLimit || showLimitError ? 'error' : 'text'}
                        sx={{ flexShrink: 0 }}
                    >
                        {plainLength}/{maxLen}
                    </MDTypography>
                )}
            </MDBox>
        </Box>
    );
};

export default HtmlFieldWithLimit;
